import type { UiTheme } from "./theme";

// A picture drawn larger than a screen (the report's pages, 1080 px wide)
// carries its scale on the theme `scaleTheme` built for it. Type, spacing and
// radii are scaled there; the few sizes that are not tokens (a chart's
// height, a stroke, a dot, a border) go through `scaled`. 1 on every screen.
export const pictureScaleOf = (theme: UiTheme) => theme.pictureScale ?? 1;

/** A size in points, in the theme's pixels. */
export const scaled = (theme: UiTheme, n: number) => n * pictureScaleOf(theme);

const times = <K extends string>(xs: Readonly<Record<K, number>>, k: number) =>
  Object.fromEntries(
    Object.entries(xs).map(([key, v]) => [key, Math.round((v as number) * k)]),
  ) as Record<K, number>;

// The strong border in the place of the plain one, same palette otherwise.
const strongBorder = <P extends { border: string; borderStrong: string }>(p: P): P => ({
  ...p,
  border: p.borderStrong,
});

/** The theme at `k` times its sizes: type, spacing and radii. No text under
 *  `textFloor` px. The system `fontScale` is divided out, so the picture is
 *  the same whatever his text size: a Text renders at size × fontScale.
 *  `strongBorders`: a 1 px plain border vanishes at 1080, a picture draws
 *  the strong one. */
export function scaleTheme(
  theme: UiTheme,
  k: number,
  {
    textFloor = 0,
    fontScale = 1,
    strongBorders = false,
  }: { textFloor?: number; fontScale?: number; strongBorders?: boolean } = {},
): UiTheme {
  const sizes = Object.fromEntries(
    Object.entries(theme.typography.sizes).map(([key, v]) => [
      key,
      Math.max(Math.round(v * k), textFloor) / fontScale,
    ]),
  ) as UiTheme["typography"]["sizes"];
  return {
    ...theme,
    palette: strongBorders ? strongBorder(theme.palette) : theme.palette,
    pictureScale: k,
    spacing: times(theme.spacing, k) as UiTheme["spacing"],
    radius: times(theme.radius, k) as UiTheme["radius"],
    typography: { ...theme.typography, sizes },
  };
}
